import { NextRequest, NextResponse } from 'next/server'
import {
  ANGEL_COOKIE_DOMAIN,
  ANGEL_DOMAIN_ANONYMOUS_ID_SYNC_COOKIE,
  ANONYMOUS_ID_HEADER,
  SEGMENT_ANONYMOUS_ID_COOKIE,
} from '@/constants/cookies'
import { Durations } from '@/constants/durations'
import { AnalyticsEvent } from '@/utils/analytics'
import { isProductionEnvironment } from '@/utils/environment-utils'
import { SegmentNodeTrackParams, track } from '../SegmentNode'
import { cloneNextUrlForPathname } from './MiddlewareNextUrlUtil'
import { NextRedirect } from './types'

export const getCookieDomain = () => (isProductionEnvironment() ? ANGEL_COOKIE_DOMAIN : undefined)

export async function redirectRequest(
  request: NextRequest,
  redirect: NextRedirect,
  trackingEvent: AnalyticsEvent,
  trackingPayload?: SegmentNodeTrackParams,
): Promise<NextResponse> {
  if (trackingPayload) {
    await track(trackingEvent, trackingPayload)
  }

  const isRelativeRedirect = redirect.destination.startsWith('/')
  const url = isRelativeRedirect ? cloneNextUrlForPathname(redirect.destination, request) : redirect.destination
  const status = redirect.permanent ? 308 : 307
  return NextResponse.redirect(url, { status })
}

export const extractAnonymousIdFromNextRequest = (request: NextRequest, onlyCheckCookies: boolean = false) => {
  const syncCookie = request.cookies.get(ANGEL_DOMAIN_ANONYMOUS_ID_SYNC_COOKIE)?.value
  const anonymousId = request.cookies.get(SEGMENT_ANONYMOUS_ID_COOKIE)?.value

  if (syncCookie) return syncCookie

  if (anonymousId) return anonymousId

  if (!onlyCheckCookies) {
    const headerAnonId = request.headers.get(ANONYMOUS_ID_HEADER)
    if (headerAnonId) return headerAnonId
  }

  return null
}

/**
 * Syncs anonymous ID cookies with the .angel.com domain.
 * Previously, angel-web set cookies without a domain, so old users may have
 * ajs_anonymous_id cookies scoped to the specific subdomain. The sync cookie
 * (with .angel.com domain) helps unify these users across subdomains.
 */
export const syncAnonymousIdCookies = (request: NextRequest, response: NextResponse, resolvedAnonymousId: string) => {
  const anonIdCookie = request.cookies.get(SEGMENT_ANONYMOUS_ID_COOKIE)?.value
  const syncCookie = request.cookies.get(ANGEL_DOMAIN_ANONYMOUS_ID_SYNC_COOKIE)?.value

  const cookieDomain = getCookieDomain()

  // convert old cookies without domains to include domains
  if ((anonIdCookie && !syncCookie) || syncCookie !== anonIdCookie) {
    response.headers.append('Set-Cookie', `${SEGMENT_ANONYMOUS_ID_COOKIE}=; Max-Age=0; Path=/`)

    response.cookies.set(SEGMENT_ANONYMOUS_ID_COOKIE, resolvedAnonymousId, {
      maxAge: Durations.TEN_YEARS_IN_SECONDS,
      path: '/',
      domain: cookieDomain,
      sameSite: 'none',
      secure: true,
    })

    response.cookies.set(ANGEL_DOMAIN_ANONYMOUS_ID_SYNC_COOKIE, resolvedAnonymousId, {
      maxAge: Durations.TEN_YEARS_IN_SECONDS,
      path: '/',
      domain: cookieDomain,
      sameSite: 'none',
      secure: true,
    })

    return
  }

  if (!anonIdCookie) {
    response.cookies.set(SEGMENT_ANONYMOUS_ID_COOKIE, resolvedAnonymousId, {
      maxAge: Durations.TEN_YEARS_IN_SECONDS,
      path: '/',
      domain: cookieDomain,
      sameSite: 'none',
      secure: true,
    })
  }

  if (!syncCookie) {
    response.cookies.set(ANGEL_DOMAIN_ANONYMOUS_ID_SYNC_COOKIE, resolvedAnonymousId, {
      maxAge: Durations.TEN_YEARS_IN_SECONDS,
      path: '/',
      domain: cookieDomain,
      sameSite: 'none',
      secure: true,
    })
  }
}
